import { CatalystInsight } from '@api';
import { POSTHOG_TRACKING_SOURCES } from '@terminal/services/tracking/posthog/constants';
import { CatalystInsightIcon } from '@terminal/widgets/catalyst-insights-drawer/catalyst-insight-icon/CatalystInsightIcon';
import * as d3 from 'd3';
import React, { useMemo } from 'react';

import styles from './InsightsOverlay.module.scss';

const CHART_MARGIN = { top: 30, right: 40, bottom: 30, left: 0 };
const ICON_SIZE = 20;
const ICON_TOP_OFFSET = 4;
const CHART_HEIGHT = 340;

export interface InsightsOverlayProps {
  insights: CatalystInsight[];
  chartWidth: number;
  minDate: Date;
  maxDate: Date;
}

interface GroupedInsight {
  insights: CatalystInsight[];
  xPosition: number;
}

const groupInsightsByDate = (
  insights: CatalystInsight[],
  xScale: d3.ScaleTime<number, number>
): GroupedInsight[] => {
  const grouped = new Map<string, CatalystInsight[]>();

  insights.forEach(insight => {
    const date = new Date(insight.created_at);
    const dateKey = date.toISOString().split('T')[0]; // "2024-01-15"

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, []);
    }

    grouped.get(dateKey)?.push(insight);
  });

  return Array.from(grouped.entries()).map(([dateKey, insights]) => ({
    insights,
    xPosition: xScale(new Date(dateKey)),
  }));
};

export const InsightsOverlay = ({
  insights,
  chartWidth,
  minDate,
  maxDate,
}: InsightsOverlayProps) => {
  const innerWidth = chartWidth - CHART_MARGIN.right - CHART_MARGIN.left;

  const xScale = useMemo(
    () => d3.scaleTime().domain([minDate, maxDate]).range([0, innerWidth]),
    [minDate, maxDate, innerWidth]
  );

  const groupedInsights = useMemo(
    () => groupInsightsByDate(insights, xScale),
    [insights, xScale]
  );

  return (
    <div className={styles.overlayContainer} data-testid="insights-overlay">
      {groupedInsights.map(group => (
        <React.Fragment key={group.xPosition}>
          {group.insights.map((insight, idx) => (
            <div
              key={insight.id}
              className={styles.insightIconWrapper}
              style={{
                left: group.xPosition + CHART_MARGIN.left,
                top:
                  CHART_HEIGHT -
                  CHART_MARGIN.bottom -
                  ICON_SIZE -
                  ICON_TOP_OFFSET -
                  idx * (ICON_SIZE + 2),
              }}
            >
              <CatalystInsightIcon
                catalystInsightId={insight.id}
                identifier={insight.identifier}
                subCategory={insight.sub_category}
                customSize={{ width: ICON_SIZE, height: ICON_SIZE }}
                isBordered
                source={POSTHOG_TRACKING_SOURCES.THEME_OVERVIEW}
                category={insight.category}
              />
            </div>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
};
